import { useContext, useEffect, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router';
import {
  type AnswerPayload,
  type OutputData,
} from '@ikea-customer-self-serve/contact-form';
import HttpStatus from 'http-status';
import { useTranslation } from 'react-i18next';
import {
  type AppointmentResponse,
  type Payload,
} from '../services/appointment/model';
import { type Answer, type Question } from '../services/additionalInfo/model';
import { ERRORS } from '../services/errors';
import { HttpError } from '../services/HttpClient';
import { useStateDispatch, useStateSelector } from '../store/';
import config from '../reducers/contact/config';
import { confirmAppointment } from '../reducers/app/appReducer';
import { showError } from '../reducers/error/errorReducer';
import apiServices from '../services';
import analyticsApiClient from '../services/analytics';
import {
  updateContactState,
  updateHasFileConsent,
} from '../reducers/contact/contactReducer';
import { addPersistingQueryParamsToPath, getPath } from '../helpers/urlHelper';
import { type ServiceValidations } from '../components/ServiceValidationMsg';
import { appendNewCookieValueForAppView } from '../helpers/appViewHelper';
import { getEventLocation, getIsMobileChanged } from '../helpers/contactHelper';
import { sendEventToParent } from '../eventPublisher';
import { getLocationType } from '../helpers/appHelper';
import { buildPartnerParams } from '../helpers/partnerParamsHelper';
import {
  selectOperationForTheSelectedTimeSlot,
  selectSelectedTimeSlot,
  selectServiceCodeForTheSelectedTimeSlot,
  selectServiceForTheSelectedTimeSlot,
  selectServiceIdForTheSelectedTimeSlot,
  selectStoreForTheSelectedTimeSlot,
  selectStoreIdForTheSelectedTimeSlot,
} from '../reducers/selectors';
import type { ContactPageLoaderData } from '../routers/loaders/contactPageLoader';
import { ViewContext } from '../context';
import { sendEventToOptimizely } from '../optimizely';

/* globals CSS_CONFIG */
const { loginRequired } = CSS_CONFIG.services;

interface UseContactState {
  isBusy: boolean;
  serviceValidations: ServiceValidations;
  mandatoryQuestions: Question[] | undefined;
}

interface UseContactActions {
  handleSubmit: (formValues: OutputData) => Promise<void>;
}

type HttpStatusType = Extract<keyof typeof HttpStatus, string>;

export default function useContact(
  operation: string,
): [UseContactState, UseContactActions] {
  const { mandatoryQuestions } = useLoaderData<ContactPageLoaderData>();
  const navigate = useNavigate();
  const serviceValidationDefaults: ServiceValidations = config;
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const operationForSelectedTimeSlot = useStateSelector(
    selectOperationForTheSelectedTimeSlot,
  );

  const [isBusy, setIsBusy] = useState(false);
  const [serviceValidations, setServiceValidations] = useState(
    serviceValidationDefaults,
  );

  const timeSlot = useStateSelector(selectSelectedTimeSlot);
  const isReplacementTimeSlot = useStateSelector(
    (state) => !!state.app.selected.replacementService.timeSlot,
  );
  const replacementServiceStoreId = useStateSelector(
    (state) => state.app.selected.replacementService.storeId,
  );
  const service = useStateSelector(selectServiceForTheSelectedTimeSlot);
  const serviceId = useStateSelector(selectServiceIdForTheSelectedTimeSlot);
  const serviceCode = useStateSelector(selectServiceCodeForTheSelectedTimeSlot);
  const store = useStateSelector(selectStoreForTheSelectedTimeSlot);
  const storeId = useStateSelector(selectStoreIdForTheSelectedTimeSlot);
  const isLoggedIn = useStateSelector((state) => state.user.isLoggedIn);
  const view = useContext(ViewContext);
  const phoneNumber = useStateSelector((state) => state.user.phoneNumber);
  const customerType = useStateSelector((state) => state.user.customerType);
  const availableServices = useStateSelector(
    (state) => state.services.availableServices,
  );
  const stores = useStateSelector((state) => state.stores.availableStores);
  const replacementServiceStores = useStateSelector(
    (state) => state.stores.replacementServiceStores,
  );
  const postCode = useStateSelector((state) => state.search.postCode);
  const selectedLanguage = useStateSelector(
    (state) => state.app.selected.language,
  );

  const errorActionPayload = {
    title: t('components.error.contact.title'),
    description: t('components.error.contact.description'),
    button: t('components.error.close'),
  };

  const activateError = (): void => {
    dispatch(showError(errorActionPayload));
    setIsBusy(false);
  };

  const showContactError = (status: HttpStatusType): void => {
    const titleTranslationKey = `components.error.contact.status.${status}.title`;
    const translatedTitle = t(titleTranslationKey);
    const isTranslated = translatedTitle !== titleTranslationKey;
    const payload = isTranslated
      ? {
          title: translatedTitle,
          description: t(
            `components.error.contact.status.${status}.description`,
          ),
          button: t('components.error.close'),
        }
      : errorActionPayload;
    dispatch(showError(payload));
    setIsBusy(false);
  };

  const handleAnswer = async (
    answerData: AnswerPayload[],
    questionsToken: string,
    orderNumber: string,
  ) => {
    if (answerData && answerData.length > 0) {
      const finalizedAnswerData: Answer[] = answerData.map(
        (ans: AnswerPayload) => {
          const consent = ans.hasConsent === true;
          return {
            answer: Array.isArray(ans.answer) ? ans.answer : [ans.answer],
            questionId: ans.questionId,
            questionType: ans.questionType,
            ...(consent && { hasConsent: consent }),
          };
        },
      );

      await apiServices.additionalInfo.postAnswers(
        questionsToken,
        finalizedAnswerData,
        orderNumber,
      );
    }
  };

  const handleSubmit = async (formValues: OutputData): Promise<void> => {
    setServiceValidations(serviceValidationDefaults);

    try {
      setIsBusy(true);
      const { appointmentId, id, dateFrom } = timeSlot || {};

      dispatch(updateContactState(formValues.formData));
      const hasConsent = formValues?.answer?.some(
        (answer) => answer.hasConsent,
      );
      if (hasConsent) {
        dispatch(updateHasFileConsent({ hasConsent }));
      }
      const isMobileChanged =
        isLoggedIn && formValues.formData.updateProfile
          ? getIsMobileChanged(formValues.formData.mobileNumber, phoneNumber)
          : undefined;

      // Extract additionalRecipient from formData
      const { additionalRecipient, ...customerData } = formValues.formData;

      const partnerParams = buildPartnerParams();

      const payload: Payload = {
        customer: { ...customerData, isMobileChanged },
        ...(additionalRecipient && { additionalRecipient }),
        ...(Object.keys(partnerParams).length > 0 && { partnerParams }),
        appointmentId,
        eventLocation: getEventLocation(
          formValues.formData,
          t('pages.confirm.remoteServiceLocation'),
          store,
          service,
        ),
      };

      const appointment: AppointmentResponse = await dispatch(
        confirmAppointment({
          zipCode: postCode,
          serviceId,
          storeId,
          timeSlotId: id || '',
          from: dateFrom || '',
          serviceCode,
          payload,
          selectedLanguage,
          view,
        }),
      ).unwrap();

      if (
        formValues.answer &&
        appointment.questionsToken &&
        appointment.orderNo
      ) {
        await handleAnswer(
          formValues.answer,
          appointment.questionsToken,
          appointment.orderNo,
        );
      }

      if (appointment.orderNo) {
        const serviceLocation = service?.location;

        const data = {
          questionsToken: appointment.questionsToken,
          orderNo: appointment.orderNo,
          appointmentId,
          startTime: timeSlot?.dateFrom,
          endTime: timeSlot?.dateTo,
          locationType: serviceLocation ? getLocationType(serviceLocation) : '',
          serviceId,
        };

        appendNewCookieValueForAppView(
          { orderNumber: appointment.orderNo },
          view,
        );
        sendEventToParent('BOKA/BOOKING_COMPLETE', view, data);
        const path = getPath(operation, 'confirm', view);
        navigate(
          addPersistingQueryParamsToPath(path, {
            exclude: ['vpc', 'projectid', 'imagination', 'event_action'],
          }),
        );
      } else {
        activateError();
      }
    } catch (err: unknown) {
      if (
        err instanceof HttpError &&
        err.response?.status === ERRORS.BAD_REQUEST &&
        typeof err.response.data === 'object' &&
        err.response.data !== null
      ) {
        const errorData = err.response.data as Record<string, unknown>;
        const serviceValidationErrs = Object.keys(
          serviceValidationDefaults,
        ).reduce(
          (acc, name): ServiceValidations => ({
            ...acc,
            [name]: !!errorData[name],
          }),
          {},
        );

        setServiceValidations({
          ...serviceValidations,
          ...serviceValidationErrs,
        });
      } else {
        const status = (err as HttpError).status
          ? (HttpStatus[
              `${(err as HttpError).status}_NAME` as HttpStatusType
            ] as HttpStatusType)
          : (HttpStatus[`${err}_NAME` as HttpStatusType] as HttpStatusType);

        showContactError(status);
        setIsBusy(false);
      }
    }
  };

  useEffect(() => {
    if (!!serviceId && loginRequired.includes(serviceId)) {
      if (isLoggedIn) {
        const { sendEventToEpisod } = analyticsApiClient();

        let replacementServiceId;
        if (isReplacementTimeSlot) {
          const replacementService = availableServices.find(
            (nextService) => nextService.serviceId === serviceId,
          )?.replacementService;
          replacementServiceId = replacementService?.serviceId;
        }

        const customPayload = {
          postalCode: postCode,
          step: '3',
          options: {
            serviceId,
            replacementServiceId,
            services: availableServices,
            storeId: isReplacementTimeSlot
              ? replacementServiceStoreId
              : storeId,
            stores: isReplacementTimeSlot ? replacementServiceStores : stores,
          },
        };
        const prefix = isReplacementTimeSlot
          ? 'select_replacement_date_and_time_'
          : 'select_date_and_time_';
        const eventName = `${prefix}${isReplacementTimeSlot ? replacementServiceId : serviceId}_${operationForSelectedTimeSlot}_appointment`;
        sendEventToEpisod(eventName, customPayload);
        sendEventToOptimizely(eventName);
      } else {
        const navigateBack = async (): Promise<void> => {
          const { id } = timeSlot || {};
          if (id) await apiServices.timeSlot.clearReservedTimeSlot(id);
          const path = `/${operation}/timeslots/`;
          navigate(addPersistingQueryParamsToPath(path));
        };
        navigateBack();
      }
    }
  }, [
    availableServices,
    isLoggedIn,
    operationForSelectedTimeSlot,
    serviceId,
    timeSlot,
    postCode,
    storeId,
    stores,
    customerType,
    operation,
    isReplacementTimeSlot,
    replacementServiceStoreId,
    replacementServiceStores,
    navigate,
  ]);

  return [
    {
      isBusy,
      serviceValidations,
      mandatoryQuestions,
    },
    { handleSubmit },
  ];
}
